import { useCallback, useEffect, useState } from 'react';

/**
 * Per-page UI state (search text, filters, sort, pagination) that must survive
 * navigating away from a list page and back.
 *
 * Backed by sessionStorage: survives a page refresh, scoped to the browser tab,
 * cleared when the tab closes and on login/logout.
 *
 * Only store JSON-safe values (ids, strings, numbers, "YYYY-MM-DD" dates) —
 * never dayjs/Date objects.
 */

const PREFIX = 'viewState.';
const cache = new Map<string, unknown>();

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

export const viewState = {
  get<T>(key: string, initial: T): T {
    if (!cache.has(key)) {
      try {
        const raw = sessionStorage.getItem(PREFIX + key);
        if (raw != null) cache.set(key, JSON.parse(raw));
      } catch {
        // Corrupt value or storage unavailable: fall back to initial
      }
    }
    if (!cache.has(key)) return initial;

    const stored = cache.get(key) as T;
    // Merge so fields added to a filter object after the value was stored get their defaults
    return isPlainObject(initial) && isPlainObject(stored) ? { ...initial, ...stored } : stored;
  },

  set<T>(key: string, value: T) {
    cache.set(key, value);
    try {
      sessionStorage.setItem(PREFIX + key, JSON.stringify(value));
    } catch {
      // Storage full or unavailable: in-memory cache still works for this session
    }
  },

  /** Forget everything, e.g. so filters don't carry over between users on a shared workstation */
  clearAll() {
    cache.clear();
    try {
      Object.keys(sessionStorage)
        .filter(k => k.startsWith(PREFIX))
        .forEach(k => sessionStorage.removeItem(k));
    } catch {
      // ignore
    }
  },
};

/**
 * Drop-in replacement for useState whose value is persisted under `key`.
 * Pass an undefined key to get plain, non-persisted useState behaviour
 * (e.g. a table embedded in a detail page).
 *
 * `initial` should be a stable value (module-level constant or primitive):
 * it is what `reset()` restores.
 */
export function useViewState<T>(key: string | undefined, initial: T) {
  const [value, setValue] = useState<T>(() => (key ? viewState.get(key, initial) : initial));

  useEffect(() => {
    if (key) viewState.set(key, value);
  }, [key, value]);

  const reset = useCallback(() => setValue(initial), []); // eslint-disable-line react-hooks/exhaustive-deps

  return [value, setValue, reset] as const;
}

/** Builds a sub-key, or undefined when the table isn't persisted */
export const viewKeyOf = (viewKey: string | undefined, name: string) =>
  viewKey ? `${viewKey}.${name}` : undefined;
